/**
 * Class positioning utilities for Schedule Grid
 *
 * Calculates precise positioning and sizing for class blocks
 * based on their actual start/end times within time slots
 */

/**
 * Convert time string (HH:MM or HH:MM:SS) to minutes from midnight
 */
export const timeToMinutes = (time: string): number => {
  const parts = time.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  return hours * 60 + minutes;
};

/**
 * Calculate the position and height of a class block within a grid cell
 *
 * @param classStartTime - Class start time (HH:MM or HH:MM:SS)
 * @param classEndTime - Class end time (HH:MM or HH:MM:SS)
 * @param slotStartTime - Time slot start time (HH:MM)
 * @param slotEndTime - Time slot end time (HH:MM)
 * @returns Object with top position (%) and height (%)
 */
export const calculateBlockPosition = (
  classStartTime: string,
  classEndTime: string,
  slotStartTime: string,
  slotEndTime: string
): { top: number; height: number } => {
  // Convert all times to minutes
  const classStart = timeToMinutes(classStartTime);
  const classEnd = timeToMinutes(classEndTime);
  const slotStart = timeToMinutes(slotStartTime);
  const slotEnd = timeToMinutes(slotEndTime);

  const slotDuration = slotEnd - slotStart;

  // Calculate top position as percentage of slot
  // If class starts before slot, top is 0
  // Otherwise, calculate how far into the slot it starts
  const topOffset = Math.max(0, classStart - slotStart);
  const topPercent = (topOffset / slotDuration) * 100;

  // Calculate height as percentage of slot
  // If class ends after slot, extend to bottom
  // Otherwise, calculate the portion it occupies
  const classStartInSlot = Math.max(classStart, slotStart);
  const classEndInSlot = Math.min(classEnd, slotEnd);
  const heightMinutes = classEndInSlot - classStartInSlot;
  const heightPercent = (heightMinutes / slotDuration) * 100;

  return {
    top: topPercent,
    height: heightPercent,
  };
};

/**
 * Check if a class overlaps with a given time slot
 */
export const classOverlapsSlot = (
  classStartTime: string,
  classEndTime: string,
  slotStartTime: string,
  slotEndTime: string
): boolean => {
  const classStart = timeToMinutes(classStartTime);
  const classEnd = timeToMinutes(classEndTime);
  const slotStart = timeToMinutes(slotStartTime);
  const slotEnd = timeToMinutes(slotEndTime);

  // Class overlaps if it starts before slot ends AND ends after slot starts
  return classStart < slotEnd && classEnd > slotStart;
};

/**
 * Check if two classes overlap in time
 */
export const classesOverlap = (
  class1StartTime: string,
  class1EndTime: string,
  class2StartTime: string,
  class2EndTime: string
): boolean => {
  const class1Start = timeToMinutes(class1StartTime);
  const class1End = timeToMinutes(class1EndTime);
  const class2Start = timeToMinutes(class2StartTime);
  const class2End = timeToMinutes(class2EndTime);

  // Classes overlap if one starts before the other ends
  return class1Start < class2End && class1End > class2Start;
};

/**
 * Interface for class with column position information
 */
export interface ClassWithPosition<T> {
  scheduledClass: T;
  columnIndex: number;
  totalColumns: number;
}

/**
 * Detect and group concurrent classes, sorted alphabetically by room name
 *
 * @param classes - Array of scheduled classes
 * @returns Array of classes with their column position information
 */
export const detectConcurrentClasses = <
  T extends {
    start_time: string;
    end_time: string;
    room_details: { room_name: string };
  },
>(
  classes: T[]
): ClassWithPosition<T>[] => {
  const result: ClassWithPosition<T>[] = [];

  classes.forEach(currentClass => {
    // Find all classes that overlap with the current class
    const overlappingClasses = classes.filter(otherClass =>
      classesOverlap(
        currentClass.start_time,
        currentClass.end_time,
        otherClass.start_time,
        otherClass.end_time
      )
    );

    // Sort overlapping classes alphabetically by room name
    const sortedOverlapping = overlappingClasses.sort((a, b) =>
      a.room_details.room_name.localeCompare(b.room_details.room_name)
    );

    // Find the index of the current class in the sorted array
    const columnIndex = sortedOverlapping.findIndex(c => c === currentClass);
    const totalColumns = sortedOverlapping.length;

    result.push({
      scheduledClass: currentClass,
      columnIndex,
      totalColumns,
    });
  });

  return result;
};

/**
 * Calculate column positioning for a class based on its concurrent position
 *
 * @param columnIndex - Index of this class among concurrent classes (0-based)
 * @param totalColumns - Total number of concurrent classes
 * @returns Object with left position (%) and width (%)
 */
export const calculateColumnPosition = (
  columnIndex: number,
  totalColumns: number
): { leftPercent: number; widthPercent: number } => {
  const widthPercent = (1 / totalColumns) * 100;
  const leftPercent = (columnIndex / totalColumns) * 100;

  return {
    leftPercent,
    widthPercent,
  };
};
